<div class="fixed lg:sticky top-0 bottom-0 lg:left-full w-full sm:w-96 lg:w-16 lg:h-screen lg:shadow">
    <div
        class="flex flex-col w-full sm:w-96 h-full transform transition-transform duration-400 ease-drawer bg-card"
        [ngClass]="{
            '-translate-x-full sm:-translate-x-96 lg:-translate-x-80 shadow': opened,
            'translate-x-0': !opened
        }">

        <!-- Header -->
        <div class="quick-chat-header flex flex-0 items-center justify-start cursor-pointer"
             (click)="toggle()">

            <!-- Default header: closed OR open without selection -->
            <ng-container *ngIf="!opened || (opened && !selectedChat)">
                <div class="flex flex-auto items-center justify-center">
                    <div class="flex flex-0 items-center justify-center w-16">
                        <mat-icon class="icon-size-6"
                                  [svgIcon]="'heroicons_outline:chat-alt-2'"></mat-icon>
                    </div>
                    <div class="text-lg font-medium text-secondary">Team Chat</div>
                    <button class="ml-auto mr-4" mat-icon-button>
                        <mat-icon [svgIcon]="'heroicons_outline:x'"></mat-icon>
                    </button>
                </div>
            </ng-container>

            <!-- Selected chat header -->
            <ng-container *ngIf="opened && selectedChat">
                <div class="flex flex-auto items-center ml-3">
                    <div class="relative flex flex-0 items-center justify-center w-10 h-10">
                        <div class="flex items-center justify-center w-full h-full rounded-full text-lg uppercase bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200"
                             [class.presence-ring]="!selectedChat.isGroup"
                             [class.online]="selectedChat.otherOnline">
                            <mat-icon *ngIf="selectedChat.isGroup"
                                      [svgIcon]="'heroicons_outline:users'"></mat-icon>
                            <span *ngIf="!selectedChat.isGroup">
                                {{ (selectedChat.name || '?').charAt(0) }}
                            </span>
                        </div>
                        <span *ngIf="!selectedChat.isGroup"
                              class="presence-dot"
                              [class.online]="selectedChat.otherOnline"></span>
                    </div>
                    <div class="ml-4 truncate">
                        <div class="text-lg font-medium leading-5 truncate">{{ selectedChat.name }}</div>
                        <div class="text-xs text-secondary">
                            <ng-container *ngIf="selectedChat.isGroup">
                                {{ selectedChat.members.length }} members
                            </ng-container>
                            <ng-container *ngIf="!selectedChat.isGroup">
                                {{ selectedChat.otherOnline ? 'Online' : 'Offline' }}
                            </ng-container>
                        </div>
                    </div>
                    <button class="ml-auto mr-4" mat-icon-button>
                        <mat-icon [svgIcon]="'heroicons_outline:x'"></mat-icon>
                    </button>
                </div>
            </ng-container>
        </div>

        <!-- Content -->
        <div class="flex flex-auto border-t overflow-hidden">

            <!-- Avatar strip — Groups + Users (live presence) -->
            <div class="flex-0 w-16 h-full overflow-y-auto overscroll-y-contain sm:overflow-hidden sm:overscroll-auto"
                 fuseScrollbar
                 [fuseScrollbarOptions]="{ wheelPropagation: false }">
                <div class="flex-auto">

                    <!-- Group conversations (avatar = group icon) -->
                    <ng-container *ngFor="let chat of chats; trackBy: trackByFn">
                        <div *ngIf="chat.isGroup"
                             class="flex items-center py-3 px-4 cursor-pointer"
                             [matTooltip]="chat.name || ''"
                             matTooltipPosition="left"
                             [ngClass]="{
                                 'hover:bg-gray-100 dark:hover:bg-hover':
                                    !selectedChat || selectedChat.id !== chat.id,
                                 'bg-primary-50 dark:bg-hover':
                                    selectedChat && selectedChat.id === chat.id
                             }"
                             (click)="selectChat(chat.id)">
                            <div class="relative flex flex-0 items-center justify-center w-8 h-8">
                                <div *ngIf="chat.unreadCount > 0"
                                     class="absolute bottom-0 right-0 flex-0 w-2 h-2 -ml-0.5 rounded-full ring-2 ring-bg-card dark:ring-gray-900 bg-primary dark:bg-primary-500 text-on-primary"
                                     [class.ring-primary-50]="selectedChat && selectedChat.id === chat.id"></div>
                                <div class="flex items-center justify-center w-full h-full rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200">
                                    <mat-icon class="icon-size-5"
                                              [svgIcon]="'heroicons_outline:users'"></mat-icon>
                                </div>
                            </div>
                        </div>
                    </ng-container>

                    <!-- Divider between groups and users -->
                    <div *ngIf="hasGroups() && users.length"
                         class="mx-3 my-2 border-t"></div>

                    <!-- Users (live online/offline) -->
                    <ng-container *ngFor="let u of users; trackBy: trackByFn">
                        <div class="flex items-center py-3 px-4 cursor-pointer"
                             [matTooltip]="u.name + (u.isOnline ? ' • Online' : ' • Offline') + (unreadForUser(u) > 0 ? ' • ' + unreadForUser(u) + ' new' : '')"
                             matTooltipPosition="left"
                             [ngClass]="{
                                 'hover:bg-gray-100 dark:hover:bg-hover': !isUserActive(u),
                                 'bg-primary-50 dark:bg-hover': isUserActive(u)
                             }"
                             (click)="openUser(u)">
                            <div class="relative flex flex-0 items-center justify-center w-8 h-8">
                                <div class="presence-ring flex items-center justify-center w-full h-full rounded-full text-sm uppercase bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200"
                                     [class.online]="u.isOnline">
                                    {{ (u.name || '?').charAt(0) }}
                                </div>
                                <span class="presence-dot" [class.online]="u.isOnline"></span>

                                <!-- Unread badge -->
                                <span *ngIf="unreadForUser(u) > 0"
                                      class="unread-badge">
                                    {{ unreadForUser(u) > 9 ? '9+' : unreadForUser(u) }}
                                </span>
                            </div>
                        </div>
                    </ng-container>

                    <div *ngIf="!users.length && !chats?.length"
                         class="text-center text-xs text-secondary py-4 px-1">
                        No users
                    </div>
                </div>
            </div>

            <!-- Conversation panel -->
            <div class="flex flex-col flex-auto border-l overflow-hidden bg-gray-50 dark:bg-transparent">

                <!-- Search bar (always visible) -->
                <div class="flex-0 flex items-center px-3 py-2 border-b bg-card dark:bg-gray-900">
                    <div class="relative flex items-center w-full">
                        <mat-icon class="icon-size-4 absolute left-2 text-secondary"
                                  [svgIcon]="'heroicons_solid:search'"></mat-icon>
                        <input type="text"
                               class="quick-chat-search w-full pl-8 pr-8 py-1.5 rounded-full border bg-gray-100 dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                               placeholder="Search users to chat..."
                               [(ngModel)]="searchTerm" />
                        <button *ngIf="searchTerm"
                                class="absolute right-1"
                                mat-icon-button
                                (click)="clearSearch()">
                            <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:x'"></mat-icon>
                        </button>
                    </div>
                </div>

                <!-- Search results overlay -->
                <ng-container *ngIf="isSearching()">
                    <div class="flex-auto overflow-y-auto bg-card dark:bg-default">

                        <!-- Group hits -->
                        <ng-container *ngIf="searchGroups().length">
                            <div class="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-secondary border-b bg-gray-50 dark:bg-gray-900">
                                Groups
                            </div>
                            <div *ngFor="let c of searchGroups(); trackBy: trackByFn"
                                 class="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-hover border-b"
                                 (click)="pickGroup(c)">
                                <div class="relative flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200">
                                    <mat-icon class="icon-size-5" [svgIcon]="'heroicons_outline:users'"></mat-icon>
                                </div>
                                <div class="ml-3 flex-1 min-w-0">
                                    <div class="text-sm font-medium truncate">{{ c.name }}</div>
                                    <div class="text-xs text-secondary truncate">
                                        {{ c.members?.length || 0 }} members
                                    </div>
                                </div>
                                <span *ngIf="c.unreadCount > 0"
                                      class="ml-2 px-1.5 rounded-full bg-primary text-on-primary text-xs">
                                    {{ c.unreadCount }}
                                </span>
                            </div>
                        </ng-container>

                        <!-- User hits -->
                        <ng-container *ngIf="searchUsers().length">
                            <div class="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-secondary border-b bg-gray-50 dark:bg-gray-900">
                                Users
                            </div>
                            <div *ngFor="let u of searchUsers(); trackBy: trackByFn"
                                 class="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-hover border-b"
                                 (click)="pickUser(u)">
                                <div class="presence-ring relative flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-gray-600 uppercase dark:bg-gray-700 dark:text-gray-200"
                                     [class.online]="u.isOnline">
                                    {{ (u.name || '?').charAt(0) }}
                                    <span class="presence-dot" [class.online]="u.isOnline"></span>
                                </div>
                                <div class="ml-3 flex-1 min-w-0">
                                    <div class="text-sm font-medium truncate">{{ u.name }}</div>
                                    <div class="text-xs text-secondary truncate">{{ u.email }}</div>
                                </div>
                                <span class="ml-2 text-[10px] font-semibold uppercase"
                                      [class.text-green-600]="u.isOnline"
                                      [class.text-gray-400]="!u.isOnline">
                                    {{ u.isOnline ? 'Online' : 'Offline' }}
                                </span>
                            </div>
                        </ng-container>

                        <!-- No results -->
                        <div *ngIf="!searchUsers().length && !searchGroups().length"
                             class="flex flex-col items-center justify-center py-10 text-secondary">
                            <mat-icon class="icon-size-10" [svgIcon]="'heroicons_outline:search-circle'"></mat-icon>
                            <div class="mt-2 text-sm">No matches</div>
                        </div>
                    </div>
                </ng-container>

                <!-- Active conversation / empty state (only when NOT searching) -->
                <ng-container *ngIf="selectedChat; else selectChatOrStartNew">
                    <div *ngIf="!isSearching()" class="flex flex-col-reverse overflow-y-auto overscroll-y-contain">
                        <div class="flex flex-col flex-auto flex-shrink p-6">

                            <ng-container *ngFor="let message of messages; let i = index; let first = first; let last = last; trackBy: trackByFn">
                                <!-- Day separator -->
                                <ng-container *ngIf="first || (messages[i-1].createdOn | date:'d') !== (message.createdOn | date:'d')">
                                    <div class="flex items-center justify-center my-3 -mx-6">
                                        <div class="flex-auto border-b"></div>
                                        <div class="flex-0 mx-4 text-sm font-medium leading-5 text-secondary">
                                            {{ message.createdOn | date:'longDate' }}
                                        </div>
                                        <div class="flex-auto border-b"></div>
                                    </div>
                                </ng-container>

                                <div class="flex flex-col"
                                     [ngClass]="{
                                         'items-end': isMine(message),
                                         'items-start': !isMine(message),
                                         'mt-0.5': i > 0 && isMine(messages[i-1]) === isMine(message),
                                         'mt-3':   i > 0 && isMine(messages[i-1]) !== isMine(message)
                                     }">

                                    <!-- Sender + time row above the bubble.
                                         For "mine" (right-aligned bubbles): time on left, name on right.
                                         For others (left-aligned bubbles):  name on left, time on right. -->
                                    <div class="flex items-center max-w-3/4 mb-0.5 px-3"
                                         [ngClass]="{
                                             'flex-row-reverse': isMine(message)
                                         }">
                                        <div class="text-xs font-semibold"
                                             [ngClass]="{
                                                 'text-blue-700': isMine(message),
                                                 'text-gray-700': !isMine(message)
                                             }">
                                            {{ isMine(message) ? 'You' : (message.senderName || 'Unknown') }}
                                        </div>
                                        <div class="text-xs text-secondary mx-2">
                                            {{ message.createdOn | date:'HH:mm' }}
                                        </div>
                                    </div>

                                    <!-- Bubble -->
                                    <div class="relative max-w-3/4 px-3 py-2 rounded-lg"
                                         [ngClass]="{
                                             'bg-blue-500 text-blue-50': isMine(message),
                                             'bg-gray-500 text-gray-50': !isMine(message)
                                         }">
                                        <ng-container *ngIf="last || messages[i+1].senderId !== message.senderId || isMine(messages[i+1]) !== isMine(message)">
                                            <div class="absolute bottom-0 w-3 transform"
                                                 [ngClass]="{
                                                    'text-blue-500 -right-1 -mr-px mb-px': isMine(message),
                                                    'text-gray-500 -left-1 -ml-px mb-px -scale-x-1': !isMine(message)
                                                 }">
                                                <ng-container *ngTemplateOutlet="speechBubbleExtension"></ng-container>
                                            </div>
                                        </ng-container>
                                        <div class="min-w-4 leading-5 whitespace-pre-wrap break-words">{{ message.content }}</div>
                                    </div>
                                </div>
                            </ng-container>

                            <div *ngIf="!messages.length" class="text-center text-secondary py-10">
                                No messages yet — say hi!
                            </div>
                        </div>
                    </div>

                    <!-- Composer -->
                    <div *ngIf="!isSearching()" class="flex items-end p-4 border-t bg-gray-50 dark:bg-transparent relative">

                        <!-- Emoji button + popover -->
                        <div class="flex items-center h-11 my-px relative">
                            <button class="emoji-toggle"
                                    mat-icon-button
                                    matTooltip="Insert emoji"
                                    (click)="toggleEmojiPicker($event)">
                                <mat-icon [svgIcon]="'heroicons_outline:emoji-happy'"></mat-icon>
                            </button>
                            <div *ngIf="showEmojiPicker"
                                 class="emoji-picker"
                                 (click)="$event.stopPropagation()">
                                <button *ngFor="let e of emojis"
                                        type="button"
                                        class="emoji-btn"
                                        (click)="insertEmoji(e)">
                                    {{ e }}
                                </button>
                            </div>
                        </div>

                        <mat-form-field class="fuse-mat-dense fuse-mat-no-subscript fuse-mat-rounded fuse-mat-bold w-full ml-2">
                            <textarea
                                class="min-h-5 my-0 resize-none"
                                style="margin: 11px 0 !important; padding: 0 !important;"
                                [rows]="1"
                                matInput
                                [(ngModel)]="draft"
                                (input)="onTyping()"
                                (keydown.enter)="onEnter($event)"
                                #messageInput></textarea>
                        </mat-form-field>
                        <div class="flex items-center h-11 my-px ml-4">
                            <button mat-icon-button [disabled]="!draft?.trim()" (click)="send()">
                                <mat-icon class="transform rotate-90"
                                          [svgIcon]="'heroicons_outline:paper-airplane'"></mat-icon>
                            </button>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>
    </div>
</div>

<!-- Empty -->
<ng-template #selectChatOrStartNew>
    <div *ngIf="!isSearching()" class="flex flex-col flex-auto items-center justify-center w-full h-full p-4">
        <mat-icon class="icon-size-20" [svgIcon]="'heroicons_outline:chat'"></mat-icon>
        <div class="mt-4 text-xl text-center font-medium tracking-tight text-secondary">
            Select a conversation
        </div>
        <div class="mt-1 text-xs text-secondary text-center">
            or use the search above to find someone
        </div>
    </div>
</ng-template>

<!-- Speech bubble tail -->
<ng-template #speechBubbleExtension>
    <svg width="100%" height="100%" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <path d="M1.01522827,0.516204834 C-8.83532715,54.3062744 61.7609863,70.5215302 64.8009949,64.3061218 C68.8074951,54.8859711 30.1663208,52.9997559 37.5036011,0.516204834 L1.01522827,0.516204834 Z" fill="currentColor" fill-rule="nonzero"></path>
        </g>
    </svg>
</ng-template>
